import { baseApi } from "@/shared/api";
import { type ExperienceDto, type ExperienceMutationResponse, type ExperienceRecord } from "./types";

function buildExperienceFormData(body: Partial<ExperienceDto>): FormData {
  const form = new FormData();

  if (body.organization_name !== undefined) form.append("organization_name", body.organization_name);
  if (body.speciality !== undefined) form.append("speciality", body.speciality);
  if (body.work_position !== undefined) form.append("work_position", body.work_position);
  if (body.work_format !== undefined) form.append("work_format", body.work_format);
  if (body.work_address !== undefined) form.append("work_address", body.work_address);
  if (body.beginning_date !== undefined) form.append("beginning_date", body.beginning_date);
  if (body.end_date !== undefined && body.end_date !== null) form.append("end_date", body.end_date);
  if (body.is_current !== undefined) form.append("is_current", String(body.is_current));

  return form;
}

export const experienceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getExperience: builder.query<ExperienceRecord[], string>({
      query: (token) => `/api/hr/apply/${token}/experience/`,
      providesTags: ["Completeness"],
    }),

    createExperience: builder.mutation<ExperienceMutationResponse, { token: string; body: Partial<ExperienceDto> }>({
      query: ({ token, body }) => ({
        url: `/api/hr/apply/${token}/experience/`,
        method: "POST",
        body: buildExperienceFormData(body),
      }),
      invalidatesTags: ["Completeness"],
    }),

    updateExperience: builder.mutation<
      ExperienceMutationResponse,
      { token: string; id: number; body: Partial<ExperienceDto> }
    >({
      query: ({ token, id, body }) => ({
        url: `/api/hr/apply/${token}/experience/${id}/`,
        method: "PATCH",
        body: buildExperienceFormData(body),
      }),
      invalidatesTags: ["Completeness"],
    }),

    deleteExperience: builder.mutation<void, { token: string; id: number }>({
      query: ({ token, id }) => ({
        url: `/api/hr/apply/${token}/experience/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Completeness"],
    }),

    setExperienceFlag: builder.mutation<void, { token: string; has_no_work_experience: boolean }>({
      query: ({ token, has_no_work_experience }) => ({
        url: `/api/hr/apply/${token}/experience-flag/`,
        method: "PATCH",
        body: { has_no_work_experience },
      }),
      invalidatesTags: ["Completeness"],
    }),

    getWorkProof: builder.query<{ work_proof_file_url: string | null; has_work_proof: boolean }, string>({
      query: (token) => `/api/hr/apply/${token}/work-proof/`,
      providesTags: ["Completeness"],
    }),

    submitWorkProof: builder.mutation<{ success: boolean }, { token: string; file: File }>({
      query: ({ token, file }) => {
        const form = new FormData();
        form.append("work_proof_file", file);
        return {
          url: `/api/hr/apply/${token}/work-proof/`,
          method: "POST",
          body: form,
        };
      },
      invalidatesTags: ["Completeness"],
    }),
  }),
});

export const {
  useGetExperienceQuery,
  useCreateExperienceMutation,
  useUpdateExperienceMutation,
  useDeleteExperienceMutation,
  useSetExperienceFlagMutation,
  useGetWorkProofQuery,
  useSubmitWorkProofMutation,
} = experienceApi;
